import { useRef } from 'react'
import { useT } from './i18n-context.js'
import { TABS, type TabKey } from '../tabs.js'

/**
 * Нижние вкладки (D10).
 *
 * Разметка — `tablist` из ARIA APG: стрелки переводят фокус между
 * вкладками, Home и End прыгают на края, активная помечена
 * `aria-selected`. Клавиатура тут не про десктоп — она про голосовой
 * доступ и внешние клавиатуры, которыми пользуются и с телефона.
 *
 * Тач-цель — `--rc-touch-target` из токенов, а не число в этом файле.
 */
export function TabBar({
  active,
  onSelect,
}: {
  readonly active: TabKey
  readonly onSelect: (tab: TabKey) => void
}) {
  const t = useT()
  const buttons = useRef(new Map<TabKey, HTMLButtonElement>())

  const move = (from: TabKey, step: number) => {
    const index = TABS.indexOf(from)
    const next = TABS[(index + step + TABS.length) % TABS.length]
    if (next === undefined) return
    onSelect(next)
    buttons.current.get(next)?.focus()
  }

  return (
    <nav className="app-tabs" aria-label={t('app.title')}>
      <div role="tablist" aria-orientation="horizontal" className="app-tabs-list">
        {TABS.map((tab) => (
          <button
            key={tab}
            type="button"
            role="tab"
            id={`tab-${tab}`}
            aria-controls={`panel-${tab}`}
            aria-selected={tab === active}
            tabIndex={tab === active ? 0 : -1}
            className="app-tab"
            ref={(node) => {
              if (node === null) buttons.current.delete(tab)
              else buttons.current.set(tab, node)
            }}
            onClick={() => {
              onSelect(tab)
            }}
            onKeyDown={(event) => {
              if (event.key === 'ArrowRight') move(tab, 1)
              else if (event.key === 'ArrowLeft') move(tab, -1)
              else if (event.key === 'Home') onSelect(TABS[0])
              else if (event.key === 'End') onSelect(TABS[TABS.length - 1] ?? TABS[0])
              else return
              event.preventDefault()
            }}
          >
            {t(`tabs.${tab}`)}
          </button>
        ))}
      </div>
    </nav>
  )
}
